package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Header ----------

val RdHeaderStyle = CssStyle.base {
    Modifier
        .position(Position.Sticky)
        .zIndex(20)
        .backgroundColor(RdColor.HeaderGlass)
        .css(
            "top" to "env(safe-area-inset-top, 0px)",
            "backdrop-filter" to "blur(10px)",
            "border-bottom" to "1px solid rgb(220,227,236)"
        )
}

val RdHeaderBarStyle = CssStyle.base {
    Modifier
        .position(Position.Relative)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.SpaceBetween)
        .gap(16.px)
        .css("min-height" to "68px")
}

val RdLogoStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Block).css("line-height" to 0) }
    cssRule(" img") { Modifier.height(40.px).css("width" to "auto") }
}

/** Below XL the navigation is a drop-down panel opened by the menu button. */
val RdNavStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.None)
            .position(Position.Absolute)
            .backgroundColor(RdColor.Paper)
            .css(
                "left" to "0", "right" to "0", "top" to "100%", "flex-direction" to "column",
                "border-bottom" to "1px solid rgb(220,227,236)", "box-shadow" to RdSize.SHADOW,
                "padding" to "8px ${RdSize.SIDE_GUTTER} 16px"
            )
    }
    Breakpoint.XL {
        Modifier
            .display(DisplayStyle.Flex)
            .position(Position.Static)
            .gap(26.px)
            .css("flex-direction" to "row", "background" to "none", "border-bottom" to "0", "box-shadow" to "none", "padding" to "0")
    }
}

val RdNavOpenStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex)
}

val RdNavLinkStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.Ink)
            .textDecorationLine(TextDecorationLine.None)
            .css("padding" to "14px 0", "border-bottom" to "1px solid rgb(220,227,236)", "font-size" to "1.05rem", "font-weight" to 500)
    }
    hover { Modifier.color(RdColor.Brand) }
    Breakpoint.XL {
        Modifier.color(RdColor.Muted).css("padding" to "0", "border-bottom" to "0", "font-size" to ".95rem")
    }
}

val RdNavLinkCurrentStyle = CssStyle.base {
    Modifier.color(RdColor.Ink).css("font-weight" to 600)
}

/** Shown inside the menu panel only on small phones, where the header button is hidden. */
val RdNavCtaStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.Brand)
            .textDecorationLine(TextDecorationLine.None)
            .css("padding" to "14px 0", "font-weight" to 600)
    }
    Breakpoint.SM { Modifier.display(DisplayStyle.None) }
}

val RdHeaderActionsStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(10.px)
}

/** Wraps the header button: hidden on small phones, where the menu panel shows it instead. */
val RdHeaderCtaStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.SM { Modifier.display(DisplayStyle.Block) }
    cssRule(" a") { Modifier.css("padding" to "11px 16px", "font-size" to ".92rem") }
}

val RdMenuButtonStyle = CssStyle {
    base {
        Modifier
            .css("display" to "inline-flex")
            .alignItems(AlignItems.Center)
            .gap(8.px)
            .color(RdColor.Ink)
            .backgroundColor(RdColor.Paper)
            .border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(10.px)
            .cursor(Cursor.Pointer)
            .css("font" to "600 .92rem/1 ${RdFont.BODY_STACK}", "padding" to "10px 12px")
    }
    Breakpoint.XL { Modifier.display(DisplayStyle.None) }
}

// ---------- Footer ----------

val RdFooterStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("padding-block" to "56px 40px", "font-size" to ".92rem")
}

val RdFooterGridStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).css("gap" to "40px 32px", "grid-template-columns" to "1fr 1fr")
    }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1.3fr repeat(3, 1fr)") }
    // The last column fits its content, so the email address stays on one line.
    // On desktop the only second row is the email under Company, so keep it close to the links like before.
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1.3fr 1fr 1fr auto", "row-gap" to "18px") }
    cssRule(" ul") { Modifier.display(DisplayStyle.Grid).gap(12.px).css("list-style" to "none", "padding" to "0", "margin" to "0", "line-height" to 1.4) }
    cssRule(" ul a") { Modifier.color(RdColor.Muted).textDecorationLine(TextDecorationLine.None) }
    cssRule(" ul a:hover") { Modifier.color(RdColor.Ink).textDecorationLine(TextDecorationLine.Underline) }
}

/** One link column: heading, links and anything extra, evenly spaced. */
val RdFooterColumnStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(18.px).css("align-content" to "start", "min-width" to "0")
}

/** The email address, picked out in brand blue, with a button to copy it. */
val RdFooterEmailStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).gap(10.px).alignItems(AlignItems.Center)
            .backgroundColor(RdColor.SkySoft).border(1.px, LineStyle.Solid, RdColor.LogoBlue).borderRadius(10.px)
            // Full width across the bottom on phones and tablets, with the copy button on the right.
            .css("grid-column" to "1 / -1", "justify-content" to "space-between", "max-width" to "100%", "padding" to "6px 6px 6px 12px")
    }
    // Desktop: under the Company links, sized to the address.
    Breakpoint.LG { Modifier.css("grid-column" to "4", "justify-self" to "start", "justify-content" to "normal", "white-space" to "nowrap") }
    cssRule(" a") {
        Modifier.display(DisplayStyle.Flex).gap(8.px).alignItems(AlignItems.Center).color(RdColor.Brand)
            .textDecorationLine(TextDecorationLine.None).css("font-weight" to 600, "min-width" to "0", "overflow-wrap" to "anywhere")
    }
    cssRule(" a:hover") { Modifier.textDecorationLine(TextDecorationLine.Underline) }
    cssRule(" svg") { Modifier.css("flex" to "none") }
}

val RdFooterLogoStyle = CssStyle.base {
    Modifier.height(44.px).css("width" to "auto")
}

val RdFooterHeadingStyle = CssStyle.base {
    Modifier
        .color(RdColor.Ink)
        .css("margin" to "0", "font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
}

val RdFooterLegalStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.SpaceBetween)
        .css(
            "flex-wrap" to "wrap", "gap" to "8px 24px", "margin-top" to "36px", "padding-top" to "20px",
            "border-top" to "1px solid rgb(220,227,236)", "font-size" to ".84rem"
        )
}

val RdFooterTaglineStyle = CssStyle.base {
    Modifier.css("margin-top" to "12px", "max-width" to "34ch")
}

